import { useMemo, useRef, useState } from "react"
import { FiSearch } from "react-icons/fi"
import { useNavigate } from "react-router"

import { areaTitle } from "./helpUtils"

import "./HelpSearch.scss"

const MAX_RESULTS = 20

export default function HelpSearch({ sitemap, availableAreas, placeholder }) {
  const navigate = useNavigate()
  const inputRef = useRef(null)
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)

  const visibleSitemap = useMemo(
    () => sitemap.filter((entry) => availableAreas.includes(entry.area)),
    [sitemap, availableAreas]
  )

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    const out = []
    const matchedPages = new Set()

    for (const entry of visibleSitemap) {
      if (entry.title.toLowerCase().includes(q)) {
        out.push({ entry, kind: "page", label: entry.title })
        matchedPages.add(`${entry.area}/${entry.slug}`)
        if (out.length >= MAX_RESULTS) return out
      }
    }
    for (const entry of visibleSitemap) {
      if (!entry.headings) continue
      for (const h of entry.headings) {
        if (h.text.toLowerCase().includes(q)) {
          out.push({ entry, kind: "heading", label: h.text, hash: h.slug })
          matchedPages.add(`${entry.area}/${entry.slug}`)
          if (out.length >= MAX_RESULTS) return out
        }
      }
    }
    for (const entry of visibleSitemap) {
      if (!entry.body) continue
      const key = `${entry.area}/${entry.slug}`
      if (matchedPages.has(key)) continue
      const idx = entry.body.toLowerCase().indexOf(q)
      if (idx === -1) continue
      const start = Math.max(0, idx - 40)
      const end = Math.min(entry.body.length, idx + q.length + 60)
      const snippet =
        (start > 0 ? "…" : "") +
        entry.body.slice(start, end) +
        (end < entry.body.length ? "…" : "")
      // Anchor body hit to the nearest heading that precedes the match.
      let anchor = null
      if (entry.headings) {
        for (const h of entry.headings) {
          if (h.bodyOffset !== undefined && h.bodyOffset <= idx) anchor = h.slug
          else break
        }
      }
      out.push({ entry, kind: "body", label: snippet, hash: anchor })
      if (out.length >= MAX_RESULTS) return out
    }
    return out
  }, [query, visibleSitemap])

  function goTo(result) {
    setQuery("")
    setOpen(false)
    inputRef.current?.blur()
    const base = `/help/${result.entry.area}/${result.entry.slug}`
    navigate(result.hash ? `${base}#${result.hash}` : base)
  }

  return (
    <div className="HelpSearch">
      <FiSearch className="HelpSearch-icon" aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={query}
        placeholder={placeholder || "Search help"}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="HelpSearch-input"
        aria-label="Search help pages"
      />
      {open && results.length > 0 && (
        <ul className="HelpSearch-results" role="listbox">
          {results.map((r, i) => (
            <li key={`${r.entry.area}/${r.entry.slug}/${r.hash || "page"}/${i}`}>
              <button
                type="button"
                className={`HelpSearch-result HelpSearch-result-${r.kind}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => goTo(r)}
              >
                <span className="HelpSearch-resultTitle">{r.label}</span>
                <span className="HelpSearch-resultArea">
                  {areaTitle(r.entry.area)}
                  {r.kind === "page" ? "" : ` · ${r.entry.title}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query.trim() && results.length === 0 && (
        <div className="HelpSearch-empty">No matches</div>
      )}
    </div>
  )
}
